import { Maybe, maybe, rd } from '@passionware/monads';
import { useSimpleEventSubscription } from '@passionware/simple-event';
import { CommandLoading } from 'cmdk';
import { groupBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from 'v5/design-system/Command';
import { cn } from 'v5/platform/dom/cn';
import { Icon } from '../../../shared';
import { selectAppId } from '../../../store/v1/auth/auth.selectors';
import { openModal } from '../../../store/v1/ui/ui.actions';
import SYSTEM_COLOURS from '../../../v1/helpers/consts/SYSTEM_COLOURS';
import { withinApp } from '../../../v4/core/appRoutes';
import Avatar from '../../../v4/shared/components/Avatar/Avatar';
import { GlobalSearchEntry } from '../../api/global-search/global-search.api';
import { Kbd } from '../../design-system/Kbd';
import { LoadingSpinner } from '../../design-system/LoadingSpinner';
import { ControlKey } from '../../primitives/ModifierKey';
import { GlobalSearchService } from '../../services/GlobalSearchService/GlobalSearchService';
import { AccessService } from '../../services/front/AccessService/AccessService';
import { Permission } from '../../services/front/PermissionService/PermissionService';
import { UserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { UserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { KeyboardShortcut } from 'v5/patterns/widgets/KeyboardShortcut';
import type { AppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

export interface CommandBarWidgetProps {
  services: {
    globalSearchService: GlobalSearchService;
    accessService: AccessService;
    userPreferencesService: UserPreferencesService;
    userFlowService: UserFlowService;
    appScopeService: AppScopeService;
    enterpriseService: EnterpriseService;
  };
}

// A helper component to register a hotkey without rendering anything.
function Hotkey({ keys, onKey }: { keys: string; onKey: () => void }) {
  useHotkeys(
    keys,
    event => {
      event.preventDefault();
      onKey();
    },
    { enableOnFormTags: true }
  );
  return null;
}

export function CommandBarWidget(props: CommandBarWidgetProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, debounced] = useDebounce(search, 200);

  const shortcuts = props.services.userPreferencesService.useShortcuts();

  const recents = props.services.globalSearchService.useRecentSearches();
  const rawSearchResults = props.services.globalSearchService.useSearch(
    maybe.fromTruthy(search === '' ? search : debouncedSearch)
  );
  const _searchResults = rd.useLastWithPlaceholder(rawSearchResults);
  const searchResults = rd.isIdle(rawSearchResults)
    ? rawSearchResults
    : _searchResults;

  // Access control
  const { accessService, appScopeService, enterpriseService, userFlowService } =
    props.services;
  const can = accessService.useCan();
  const appId = useSelector(selectAppId);
  const scopeRd = appScopeService.useAppScope();
  const scope = rd.tryGet(scopeRd);
  const workspacesRd = enterpriseService.useWorkspaces(appId);
  const workspaces = rd.tryGet(workspacesRd);
  const teamsRd = enterpriseService.useTeams(
    scope && scope.level !== 'organization'
      ? maybe.of({
          organizationId: appId,
          workspaceId: scope.workspaceId
        })
      : maybe.ofAbsent()
  );
  const teams = rd.tryGet(teamsRd)?.map(team => ({
    id: team.id,
    slug: team.slug
  }));
  const scopedWorkspaceId =
    scope && scope.level !== 'organization' ? scope.workspaceId : undefined;
  const scopedTeamId = scope?.level === 'team' ? scope.teamId : undefined;

  const permissionsByCategory = {
    production: {
      read: Permission.PRODUCTION_READ,
      create: Permission.PRODUCTION_CREATE
    },
    contact: {
      read: Permission.RESOURCE_READ,
      create: Permission.RESOURCE_CREATE
    },
    resource: {
      read: Permission.RESOURCE_READ,
      create: Permission.RESOURCE_CREATE
    },
    shortlist: {
      read: Permission.SHORTLIST_READ,
      create: Permission.SHORTLIST_CREATE
    },
    callsheet: {
      read: Permission.CALLSHEET_READ,
      create: Permission.CALLSHEET_CREATE
    },
    callout: {
      read: Permission.CALLOUT_READ,
      create: Permission.CALLOUT_CREATE
    },
    booking: {
      read: Permission.BOOKING_READ,
      create: Permission.BOOKING_CREATE
    }
  } satisfies Record<string, Record<'read' | 'create', Permission>>;

  const resultsByGroup = rd.map(searchResults, results =>
    Object.entries(groupBy(results, 'group')).map(([group, results]) => ({
      group,
      results
    }))
  );
  const groupOrder = [
    'production',
    'callout',
    'contact',
    'callsheet',
    'shortlist'
  ];
  const labelByGroup = {
    production: 'Productions',
    callout: 'Callouts',
    contact: 'Contacts',
    callsheet: 'Callsheets',
    shortlist: 'Shortlists'
  };

  const sortedResultsByGroup = rd.map(resultsByGroup, groups =>
    groups.sort(
      (a, b) => groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group)
    )
  );

  useEffect(() => {
    if (open) {
      debounced.flush();
      debounced.cancel();
      setSearch('');
    }
  }, [open]);

  useSimpleEventSubscription(
    props.services.globalSearchService.openSearchEvent.addListener,
    () => {
      setOpen(true);
    }
  );

  const dispatch = useDispatch();
  const commandListRef = useRef<HTMLDivElement>(null);
  rd.useEffect(sortedResultsByGroup, data => {
    if ((rd.isIdle(data) || rd.isSuccess(data)) && commandListRef.current) {
      commandListRef.current.scrollTo({
        top: 0
      });
    }
  });

  useHotkeys(`mod+k`, () => setOpen(prev => !prev), {
    enableOnFormTags: true
  });

  // Define the static "Create" items in an array.
  const createItems = rd.mapOrElse(
    shortcuts,
    shortcuts => [
      {
        id: 'resource',
        label: 'Create resource',
        icon: <Icon name="v4_individual" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.resource} />,
        combination: shortcuts.create.resource,
        onSelect: () => {
          setOpen(false);
          userFlowService.openCreateResourceModal({
            enterpriseWorkspaceId: scopedWorkspaceId
          });
        }
      },
      {
        id: 'production',
        label: 'Create production',
        icon: <Icon name="v4_project" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.production} />,
        combination: shortcuts.create.production,
        onSelect: () => {
          setOpen(false);
          dispatch(
            openModal('ProductionSetUpModal', {
              create: true,
              production: {
                ...(scopedWorkspaceId != null
                  ? { enterprise_workspace_id: scopedWorkspaceId }
                  : {}),
                ...(scopedTeamId != null
                  ? { enterprise_team_id: scopedTeamId }
                  : {})
              },
              workspaces,
              teams
            })
          );
        }
      },
      {
        id: 'shortlist',
        label: 'Create shortlist',
        icon: <Icon name="v4_shortlist" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.shortlist} />,
        combination: shortcuts.create.shortlist,
        onSelect: () => {
          setOpen(false);
          dispatch(openModal('ShortlistCreateModal', { create: true }));
        }
      },
      {
        id: 'booking',
        label: 'Create booking',
        icon: <Icon name="v4_booking" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.booking} />,
        combination: shortcuts.create.booking,
        onSelect: () => {
          setOpen(false);
          dispatch(openModal('ResourceEventsModal'));
        }
      },
      {
        id: 'callsheet',
        label: 'Create callsheet',
        icon: <Icon name="v4_callsheet" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.callsheet} />,
        combination: shortcuts.create.callsheet,
        onSelect: () => {
          setOpen(false);
          userFlowService.openCreateCallsheetDialog(appId);
        }
      },
      {
        id: 'callout',
        label: 'Create callout',
        icon: <Icon name="v4_callout" />,
        shortcut: <KeyboardShortcut shortcut={shortcuts.create.callout} />,
        combination: shortcuts.create.callout,
        onSelect: () => {
          setOpen(false);
          void userFlowService.openCreateCalloutModal({
            enterpriseWorkspaceId: scopedWorkspaceId
          });
        }
      }
    ],
    []
  );

  // Register hotkeys for each "Create" item.
  // These hotkeys will work regardless of the command dialog being open.
  const visibleCreateItems = createItems.filter(item => {
    const permission = permissionsByCategory[item.id];
    return permission ? rd.tryGet(can({ action: permission.create })) : true;
    // if the permission is not defined explicitly, we consider it as granted by default
  });
  const createHotkeys = visibleCreateItems.map(item => (
    <Hotkey key={item.id} keys={item.combination} onKey={item.onSelect} />
  ));

  const filteredRecents = recents.filter(result => {
    const permission = permissionsByCategory[result.group ?? ''];
    return permission ? rd.tryGet(can({ action: permission.read })) : true;
    // if the permission is not defined explicitly, we consider it as granted by default
  });

  return (
    <>
      {createHotkeys}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search or create something..."
          value={search}
          onValueChange={setSearch}
          extra={
            <div
              className={cn(
                'transition-opacity duration-200 opacity-0 delay-100 mr-4',
                {
                  'opacity-100': rd.isFetching(rawSearchResults)
                }
              )}
            >
              <LoadingSpinner
                type="spin"
                color={SYSTEM_COLOURS.BLACK}
                height={15}
                width={15}
              />
            </div>
          }
        />
        <CommandList ref={commandListRef} className="max-h-[400px] ">
          {rd
            .fullJourney(sortedResultsByGroup)
            .initially(
              <>
                {filteredRecents.length > 0 && (
                  <CommandGroup heading="Recent">
                    {filteredRecents.map(result => (
                      <EntryCommandItem
                        key={result.id}
                        entry={result}
                        group={result.group}
                        services={props.services}
                        onSelect={() => setOpen(false)}
                      />
                    ))}
                  </CommandGroup>
                )}
                {visibleCreateItems.length > 0 && (
                  <CommandGroup heading="Create">
                    {visibleCreateItems.map(item => (
                      <CommandItem key={item.id} onSelect={item.onSelect}>
                        <div className="w-10 h-10 flex flex-col justify-center items-center">
                          {item.icon}
                        </div>
                        <span>{item.label}</span>
                        <Kbd className="ml-auto">{item.shortcut}</Kbd>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )
            .wait(<CommandLoading />)
            .catch(() => <CommandEmpty>Error searching</CommandEmpty>)
            .map(groups => (
              <>
                <CommandEmpty>No results found.</CommandEmpty>
                {groups
                  .filter(group => {
                    const permission = permissionsByCategory[group.group ?? ''];
                    return permission
                      ? rd.tryGet(can({ action: permission.read }))
                      : true;
                    // if the permission is not defined explicitly, we consider it as granted by default
                  })
                  .map(group => (
                    <CommandGroup
                      key={group.group}
                      heading={labelByGroup[group.group] ?? group.group}
                    >
                      {group.results.map(result => (
                        <EntryCommandItem
                          key={result.id}
                          entry={result}
                          group={group.group}
                          services={props.services}
                          onSelect={() => setOpen(false)}
                        />
                      ))}
                    </CommandGroup>
                  ))}
                <CommandSeparator />
              </>
            ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}

const iconByGroup = {
  production: <Icon name="v4_project" />,
  callout: <Icon name="v4_callout" />,
  contact: <Icon name="v4_individual" />,
  callsheet: <Icon name="v4_callsheet" />,
  shortlist: <Icon name="v4_shortlist" />
};

function EntryCommandItem(props: {
  entry: GlobalSearchEntry;
  group: Maybe<string>;
  services: {
    globalSearchService: GlobalSearchService;
  };
  onSelect: () => void;
}) {
  const { entry: result } = props;
  const { avatar } = result;
  const appId = useSelector(selectAppId);
  const history = useHistory();
  const to = withinApp(appId);

  return (
    <CommandItem
      key={`${result.group}.${result.id}`}
      value={`${result.group}.${result.id}`}
      onSelect={() => {
        props.services.globalSearchService.storeRecentSearchItem(result);
        props.onSelect();
        history.push(`${to.teamRoot()}${result.url}`);
      }}
    >
      {maybe.map(true, () => {
        if (!avatar?.url && !avatar?.ascii && !avatar?.fallback) {
          return (
            <div className="w-10 h-10 flex flex-col justify-center items-center">
              {iconByGroup[props.group ?? '']}
            </div>
          );
        }
        return (
          <Avatar
            src={avatar.url}
            fallback={avatar.fallback ?? avatar.ascii}
            className={cn(
              'size-10!',
              avatar.ascii ? '[&_*]:text-3xl' : 'bg-bg-inset '
            )}
          />
        );
      })}
      <div className="flex flex-col flex-1">
        <span>{result.name}</span>
        <span className="text-xs">{result.description}</span>
        {maybe.map(result.badge, badge => (
          <div className="self-start text-sm bg-bg-subtle border border-border-default rounded-md px-1 mt-1">
            {badge.text}
          </div>
        ))}
      </div>
    </CommandItem>
  );
}
